import { ColumnDef } from "@tanstack/react-table";
import { AxiosError, AxiosResponse } from "axios";
import React, { useEffect, useState } from "react";
import DataTable2 from "shared/DataTable2";
import { Button } from "../../components/ui/button";
import { Edit2, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/Label";
import { Toaster } from "../../components/ui/toaster";
import { useToast } from "../../hooks/use-toast";
import { MedicalGroup, Result } from "../../interface/MedicalIcd";
import { Option } from "../../interface/Option";
import { MedicalIcdAxiosInstance } from "../../Network/MedicalIcd";
import Listofmedicalcode from "../Shared/ListofIcdmedicalcode";
import ListofMedicalGroup from "../Shared/ListofMedicalGroup";
import { LoginAxiosInstance } from "../../Network/LoginAxiosInstance";

// ── Role Helper ──────────────────────────────────────────────────────────────
const getUserRole = (): string => {
  try {
    const token = localStorage.getItem("token") || "";
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.userType || "";
  } catch { return ""; }
};

// ─── QA Automation IDs ───────────────────────────────────────────────────────
// Stable selectors for Playwright / Cypress / Selenium.
// Additive only: no API, payload, validation logic, permissions, routing,
// visual style, or UI behavior changes.
const QA_PAGE_PREFIX = "medical-icd-group";

const qaSafe = (value: string | number | undefined | null) =>
  String(value ?? "unknown")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const qaId = (suffix: string | number) => `${QA_PAGE_PREFIX}-${qaSafe(suffix)}`;

const qaAttrs = (suffix: string | number) => ({
  id: qaId(suffix),
  "data-testid": qaId(suffix),
  "data-qa": qaId(suffix),
});

const rowQaId = (action: string, id: string | number | undefined) =>
  qaId(`${action}-${id ?? "unknown"}`);

// ── SectionCard Helper ───────────────────────────────────────────────────────
const SectionCard: React.FC<{
  num: number; title: string; subtitle?: string; automationId?: string; children: React.ReactNode;
}> = ({ num, title, subtitle, automationId, children }) => (
  <div
    {...(automationId ? { id: automationId, "data-testid": automationId, "data-qa": automationId } : {})}
    className="border border-gray-200 rounded-xl overflow-hidden mb-4 bg-white shadow-sm"
  >
    <div
      {...(automationId ? { id: `${automationId}-header`, "data-testid": `${automationId}-header`, "data-qa": `${automationId}-header` } : {})}
      className="flex items-center gap-3 px-5 py-3 bg-gray-50 border-b border-gray-200"
    >
      <span
        {...(automationId ? { id: `${automationId}-number`, "data-testid": `${automationId}-number`, "data-qa": `${automationId}-number` } : {})}
        className="w-6 h-6 rounded-full bg-blue-700 text-white text-xs font-semibold flex items-center justify-center flex-shrink-0"
      >
        {num}
      </span>
      <div>
        <p
          {...(automationId ? { id: `${automationId}-title`, "data-testid": `${automationId}-title`, "data-qa": `${automationId}-title` } : {})}
          className="text-sm font-semibold text-gray-800"
        >
          {title}
        </p>
        {subtitle && (
          <p
            {...(automationId ? { id: `${automationId}-subtitle`, "data-testid": `${automationId}-subtitle`, "data-qa": `${automationId}-subtitle` } : {})}
            className="text-xs text-gray-500"
          >
            {subtitle}
          </p>
        )}
      </div>
    </div>
    <div
      {...(automationId ? { id: `${automationId}-body`, "data-testid": `${automationId}-body`, "data-qa": `${automationId}-body` } : {})}
      className="p-5"
    >
      {children}
    </div>
  </div>
);

const MedicalIcdGroups: React.FC = () => {
  const [items, setItems] = useState<MedicalGroup>({ result: [] });
  const [Name, setName] = useState<string>("");
  const [Description, setDescription] = useState<string>("");
  const [medicalIcdGroups, setMedicalIcdGroups] = useState<Option[]>([]);
  const [medicalcode, setMedicalcode] = useState<Option[]>([]);
  const [IcdDescription, setIcdDescription] = useState<string>("");
  const [isActive, setIsActive] = useState<boolean>(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<null | number>(null);
  const [clearmedicalIcdGroups, setClearMedicalIcdGroups] = useState(false);
  const [clearmedicalcode, setClearMedicalcode] = useState(false);
  const [viewCodes, setViewCodes] = useState<string[]>([]);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Result | null>(null);
  const [usersMap, setUsersMap] = useState<Record<number, string>>({});
  const [filterStatus, setFilterStatus] = useState<string>("");
  const [filterName, setFilterName] = useState<string>("");
  const [filterIcdType, setFilterIcdType] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [icdCodesDialogOpen, setIcdCodesDialogOpen] = useState(false);
  const [icdCodesDialogList, setIcdCodesDialogList] = useState<Array<{ label: string; kind: "icd" | "medical" }>>([]);
  const [icdCodesDialogTitle, setIcdCodesDialogTitle] = useState("");
  const [icdSearch, setIcdSearch] = useState<string>("");
  const [forceTargetItem, setForceTargetItem] = useState<any>(null);
  const [showAllUpdates, setShowAllUpdates] = useState(false);

  // ── Role-based access ────────────────────────────────────────────────────
  const userRole = getUserRole();
  const canManageDeleted = userRole === "system_admin" || userRole === "super_super_admin";

  // ── Force Dialog state ───────────────────────────────────────────────────
  const [forceDialogOpen, setForceDialogOpen] = useState(false);
  const [forceAction, setForceAction] = useState<"softDelete" | "hardDelete" | "status" | null>(null);
  const [forceId, setForceId] = useState<string | number | null>(null);
  const [forceError, setForceError] = useState<string>("");

  const token = localStorage.getItem("token") || "";
  const { toast } = useToast();

  // ─── QA Automation: nested shared dropdowns/search ─────────────────────────
  // Some controls are rendered inside shared components / react-select.
  // This attaches stable selectors to the actual inner input/select element.
  const applyNestedControlQaAttrs = () => {
    const setControlQaAttrs = (wrapperQaId: string, controlTestId: string) => {
      const wrapper = document.getElementById(wrapperQaId);
      const control = wrapper?.querySelector("input, select, textarea") as
        | HTMLInputElement
        | HTMLSelectElement
        | HTMLTextAreaElement
        | null;

      if (control) {
        control.setAttribute("id", controlTestId);
        control.setAttribute("data-testid", controlTestId);
        control.setAttribute("data-qa", controlTestId);
      }
    };

    setControlQaAttrs(qaId("wrapper-search-name"), "medical-icd-group-input-search-name");
    setControlQaAttrs(qaId("wrapper-search-icd"), "medical-icd-group-input-search-icd");
    setControlQaAttrs(qaId("wrapper-icd-selector"), "medical-icd-group-select-icd");
    setControlQaAttrs(qaId("wrapper-medical-group-selector"), "medical-icd-group-select-medical-group");
  };

  useEffect(() => {
    const timer = window.setTimeout(applyNestedControlQaAttrs, 0);
    return () => window.clearTimeout(timer);
  }, [
    isDialogOpen,
    filterName,
    icdSearch,
    clearmedicalIcdGroups,
    clearmedicalcode,
    medicalcode.length,
    medicalIcdGroups.length,
  ]);

  // Backend group relations must be sent as database IDs only.
  const toIdArray = (options: Option[]): number[] =>
    options
      .map((option) => Number(option.value))
      .filter((id) => Number.isInteger(id) && id > 0);

  const normalizeMedicalGroupsForRequest = (groups: any[] = []): number[] => {
    if (!Array.isArray(groups)) return [];
    return groups
      .map((group) => {
        if (group == null) return NaN;
        if (typeof group === "object") return Number(group.id ?? group.value);
        return Number(group);
      })
      .filter((id) => Number.isInteger(id) && id > 0);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Fetch from the backend whenever pagination or server-side filters change.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      fetchItems();
    }, 300);

    return () => window.clearTimeout(timer);
  }, [currentPage, pageSize, filterName, filterStatus, icdSearch]);

  const fetchUsers = async () => {
    try {
      const res = await LoginAxiosInstance.get("/");
      const raw: any[] = res.data?.users ?? res.data?.user ?? res.data ?? [];
      const map: Record<number, string> = {};
      raw.forEach((u: any) => {
        if (u.id != null) map[Number(u.id)] = u.name ?? u.username ?? String(u.id);
      });
      setUsersMap(map);
    } catch {}
  };

  const resolveUserName = (id: any): string => {
    if (id == null) return "-";
    const n = Number(id);
    return usersMap[n] ?? String(id);
  };

  // Filtering is now handled by the backend.
  const filteredData = items.result;


  const handlemedicalicdChange = (selectedOptions: Option[]) => setMedicalIcdGroups(selectedOptions);
  const handlemedicalcodeChange = (selectedOptions: Option[]) => setMedicalcode(selectedOptions);

  const clear = () => {
    setName(""); setDescription(""); setMedicalIcdGroups([]); setMedicalcode([]);
    setClearMedicalIcdGroups(true); setIsActive(true);
  };

  const fetchMedicalGroupDetails = async (id: string | number, fallback?: Result): Promise<Result> => {
    const response = await MedicalIcdAxiosInstance.get(`getMedicalGroup/${id}`);
    const raw =
      response.data?.result ??
      response.data?.getMedicalGroup ??
      response.data?.data ??
      response.data?.item ??
      response.data;
    const record = Array.isArray(raw) ? raw[0] : raw;
    if (!record || typeof record !== "object") {
      throw new Error("Medical ICD Group details were not returned by the backend.");
    }
    return { ...(fallback || {}), ...record } as Result;
  };

  const handleOpenDialog = async (item: Result) => {
    try {
      setSelectedItem(await fetchMedicalGroupDetails(item.id, item));
    } catch (error) {
      console.error("Failed to load Medical ICD Group by ID for view:", error);
      setSelectedItem(item);
      toast({ title: "Warning", description: "Could not refresh group details. Using current row data." });
    }
  };

  const handleCloseDialog = () => { setSelectedItem(null); setShowAllUpdates(false); };
  const handleAddClick = () => { setEditIndex(null); clear(); setIsDialogOpen(true); };

  const handleEditClick = async (item: Result) => {
    try {
      handleEdit(await fetchMedicalGroupDetails(item.id, item));
    } catch (error) {
      console.error("Failed to load Medical ICD Group by ID for edit:", error);
      handleEdit(item);
      toast({ title: "Warning", description: "Could not refresh group details. Using current row data." });
    }
    setIsDialogOpen(true);
  };

  // ── Fetch Items ──────────────────────────────────────────────────────────
  const fetchItems = async (): Promise<void> => {
    setIsTableLoading(true);

    try {
      const response: AxiosResponse<{
        success: boolean;
        result: Result[];
        pagination?: {
          totalCount?: number | string;
          page?: number;
          limit?: number;
          totalPages?: number;
        };
      }> = await MedicalIcdAxiosInstance.get("getMedicalGroup", {
        params: {
          // DataTable uses a zero-based page index; the API uses one-based pages.
          page: currentPage + 1,
          limit: pageSize,
          name: filterName.trim() || undefined,
          disease: icdSearch.trim() || undefined,
          is_active: filterStatus || undefined,
        },
      });

      const rows = Array.isArray(response.data.result)
        ? response.data.result
        : [];

      // Preserve the exact order returned by the backend.
      setItems({ result: rows });

      const pagination = response.data.pagination;
      const nextTotalCount = Number(pagination?.totalCount ?? rows.length) || 0;
      const nextTotalPages = Number(
        pagination?.totalPages ?? Math.ceil(nextTotalCount / pageSize)
      ) || 0;

      setTotalCount(nextTotalCount);
      setTotalPages(nextTotalPages);
    } catch (error) {
      console.error("Error fetching items:", error);
      setItems({ result: [] });
      setTotalCount(0);
      setTotalPages(0);
      toast({
        title: "Error",
        description: "Failed to fetch medical ICD group",
        variant: "destructive",
      });
    } finally {
      setIsTableLoading(false);
    }
  };

  // ── Soft Delete ──────────────────────────────────────────────────────────
  const removeItem = async (id: string | number): Promise<void> => {
    try {
      const response = await MedicalIcdAxiosInstance.delete(`softDeleteMedicalGroup/${id}`, { data: { token } });
      if (response.data.success) {
        await fetchItems();
        toast({ title: "Success", description: "Medical ICD group Deleted Successfully." });
      } else {
        setForceId(id);
        setForceAction("softDelete");
        setForceError(response.data.error || "This record is used elsewhere.");
        setForceDialogOpen(true);
      }
    } catch (error: any) {
      const errMsg = error.response?.data?.error || "Failed to delete";
      if (error.response?.status === 409 || errMsg.toLowerCase().includes("used")) {
        setForceId(id); setForceAction("softDelete"); setForceError(errMsg); setForceDialogOpen(true);
      } else {
        toast({ title: "Error", description: errMsg, variant: "destructive" });
      }
    }
  };

  // ── Restore ──────────────────────────────────────────────────────────────
  const restoreItem = async (id: string | number): Promise<void> => {
    try {
      const response = await MedicalIcdAxiosInstance.patch(`restoreMedicalGroup/${id}`, { token });
      if (response.data.success) {
        await fetchItems();
        toast({ title: "Success", description: "Medical ICD group restored successfully." });
      } else {
        toast({ title: "Error", description: response.data.error || "Failed to restore", variant: "destructive" });
      }
    } catch (error: any) {
      toast({ title: "Error", description: error.response?.data?.error || "Failed to restore", variant: "destructive" });
    }
  };

  // ── Hard Delete (permanent) ──────────────────────────────────────────────
  const permanentDeleteItem = async (id: string | number, force = false): Promise<void> => {
    try {
      const url = force ? `deleteMedicalGroup/${id}?force=true` : `deleteMedicalGroup/${id}`;
      const response = await MedicalIcdAxiosInstance.delete(url, { data: { token } });
      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null);
        toast({ title: "Success", description: "Medical ICD group Permanently Deleted." });
      } else {
        setForceAction("hardDelete");
        setForceError(response.data.error || "This record is used elsewhere.");
      }
    } catch (error: any) {
      setForceAction("hardDelete");
      setForceError(error.response?.data?.error || "Failed to delete");
    }
  };

  // ── Force Soft Delete ────────────────────────────────────────────────────
  const forceSoftDelete = async (id: string | number): Promise<void> => {
    try {
      const response = await MedicalIcdAxiosInstance.delete(`softDeleteMedicalGroup/${id}?force=true`, { data: { token } });
      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null);
        toast({ title: "Success", description: "Medical ICD group force Deleted Successfully." });
      } else {
        setForceError(response.data.error || "Failed to force delete");
      }
    } catch (error: any) {
      setForceError(error.response?.data?.error || "Failed to force delete");
    }
  };

  const addItem = async (): Promise<void> => {
    if (!Name.trim()) { toast({ title: "Error", description: "Name is required and cannot be empty.", variant: "destructive" }); return; }
    if (Name.trim().length < 3) { toast({ title: "Error", description: "Name must be at least 3 characters long.", variant: "destructive" }); return; }
    if (!medicalcode.length || medicalcode.every((option) => !option.value)) { toast({ title: "Error", description: "At least one ICD code is required.", variant: "destructive" }); return; }
    try {
      const payload = {
        data: [{ name: Name, icd: medicalcode.map((o) => o.value), description: Description, medical_groups: toIdArray(medicalIcdGroups), is_active: isActive }],
        token,
      };
      const response = await MedicalIcdAxiosInstance.post("addNewMedicalGroup", payload);
      if (response.data.success) {
        await fetchItems();
        setCurrentPage(0);
        clear(); setIsDialogOpen(false);
        toast({ title: "Success", description: "New medical ICD group Added Successfully." });
      } else {
        const err = response.data.errors?.[0];
        toast({ title: "Error", description: err?.error || response.data.error || "Failed to add the group.", variant: "destructive" });
      }
    } catch (error: AxiosError | any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  const handleEdit = (item: Result) => {
    setName(item.name);
    const MedicalIcdGroupsData = Array.isArray(item.medical_groups)
      ? item.medical_groups.map((obj) => typeof obj === "string" ? { id: 0, name: obj } : obj)
      : [];
    setMedicalIcdGroups(MedicalIcdGroupsData.map((obj) => ({ value: obj.id, label: obj.name })));
    setIsActive(item.is_active);
    setMedicalcode(item.icd.map((value) => ({ value, label: value.toString() })));
    setDescription(item.description);
    setEditIndex(item.id);
  };

  const toggleActive = async (row: any, force = false): Promise<void> => {
    try {
      const url = force ? "updateMedicalGroup?force=true" : "updateMedicalGroup";
      const response = await MedicalIcdAxiosInstance.put(url, {
        data: { id: row.id, name: row.name, icd: row.icd, description: row.description || "", medical_groups: normalizeMedicalGroupsForRequest(row.medical_groups || []), is_active: !row.is_active },
        token,
      });
      if (response.data.success) {
        setItems((prev) => ({
          result: prev.result.map((i) => i.id === row.id ? { ...i, is_active: !row.is_active } : i),
        }));
        setForceDialogOpen(false); setForceId(null); setForceTargetItem(null); setForceError(""); setForceAction(null);
        toast({ title: "Success", description: `Status updated to ${!row.is_active ? "active" : "inactive"}` });
      } else {
        setForceId(row.id);
        setForceTargetItem(row);
        setForceAction("status");
        setForceError(response.data.error || "This record is used elsewhere.");
        setForceDialogOpen(true);
      }
    } catch (error: any) {
      const errMsg = error.response?.data?.error || "";
      if (error.response?.status === 409 || errMsg.toLowerCase().includes("used") || errMsg.toLowerCase().includes("referenced") || errMsg.toLowerCase().includes("cannot")) {
        setForceId(row.id);
        setForceTargetItem(row);
        setForceAction("status");
        setForceError(errMsg || "This record is used elsewhere.");
        setForceDialogOpen(true);
      } else {
        toast({ title: "Error", description: errMsg || "An unexpected error occurred.", variant: "destructive" });
      }
    }
  };

  const updateItem = async (id: string | number): Promise<void> => {
    if (!Name.trim()) { toast({ title: "Error", description: "Name is required and cannot be empty.", variant: "destructive" }); return; }
    if (Name.trim().length < 3) { toast({ title: "Error", description: "Name must be at least 3 characters long.", variant: "destructive" }); return; }
    if (!medicalcode.length || medicalcode.every((option) => !option.value)) { toast({ title: "Error", description: "At least one ICD code is required.", variant: "destructive" }); return; }
    try {
      const response = await MedicalIcdAxiosInstance.put("updateMedicalGroup", {
        data: { name: Name, icd: medicalcode.map((o) => o.value), description: Description, medical_groups: toIdArray(medicalIcdGroups), is_active: isActive, id },
        token,
      });
      if (response.data.success) {
        await fetchItems();
        setCurrentPage(0);
        clear(); setIsDialogOpen(false);
        toast({ title: "Success", description: "Medical ICD group updated successfully." });
      } else {
        (response.data.errors || []).forEach((error: any) => {
          toast({
            title: "Error",
            description: error.error === 'duplicate key value violates unique constraint "icd_midical_group_name_key"'
              ? "Name must be unique" : error.error,
            variant: "destructive",
          });
        });
      }
    } catch (error: AxiosError | any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  const columns: ColumnDef<Result>[] = [
    {
      accessorKey: "id",
      header: ({ column }) => (
        <Button {...qaAttrs("sort-id")} variant="ghost" className="w-full justify-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Id{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
    cell: ({ row }) => (
  <div
    id={rowQaId("cell-id", row.original.id)}
    data-testid={rowQaId("cell-id", row.original.id)}
    data-qa={rowQaId("cell-id", row.original.id)}
    className="w-full flex justify-center font-medium"
  >
    {row.original.id}
  </div>
),
    },
    {
      accessorKey: "name",
      header: ({ column }) => (
        <Button {...qaAttrs("sort-name")} variant="ghost" className="w-full justify-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Name{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const isDeleted = (row.original as any).is_deleted;
        return (
          <div
            id={rowQaId("cell-name", row.original.id)}
            data-testid={rowQaId("cell-name", row.original.id)}
            data-qa={rowQaId("cell-name", row.original.id)}
            className={`w-full text-center ${isDeleted ? "line-through text-gray-400" : ""}`}
          >
            {row.getValue("name")}
          </div>
        );
      },
    },
    {
      accessorKey: "description",
      header: ({ column }) => (
        <Button {...qaAttrs("sort-description")} variant="ghost" className="w-full justify-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Description{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => (
        <div
          id={rowQaId("cell-description", row.original.id)}
          data-testid={rowQaId("cell-description", row.original.id)}
          data-qa={rowQaId("cell-description", row.original.id)}
          className="w-full text-center"
        >
          {row.getValue("description")}
        </div>
      ),
    },
    {
      accessorKey: "icd",
      header: ({ column }) => (
        <Button {...qaAttrs("sort-icd")} variant="ghost" className="w-full justify-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          ICD{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        const icdCodes = Array.isArray(item.icd) ? item.icd : [];
        const icdMedical = Array.isArray(item.medical_groups) ? item.medical_groups.map((g: any) => g.name || g) : [];

        const allDisplay = [
          ...icdCodes.map((c: string) => ({ label: c, kind: "icd" as const })),
          ...icdMedical.map((g: string) => ({ label: g, kind: "medical" as const })),
        ];

        const total = allDisplay.length;
        if (total === 0) return <div id={rowQaId("cell-icd-empty", item.id)} data-testid={rowQaId("cell-icd-empty", item.id)} data-qa={rowQaId("cell-icd-empty", item.id)} className="w-full text-center text-gray-400">—</div>;
        const shown = allDisplay.slice(0, 3);

        const kindStyle = (kind: "icd" | "medical") => {
          if (kind === "icd") return { bg: "#011934", color: "#fff", border: "#011934", tag: "ICD" };
          return { bg: "#f0fdf4", color: "#15803d", border: "#86efac", tag: "MED" };
        };

        return (
          <div id={rowQaId("cell-icd", item.id)} data-testid={rowQaId("cell-icd", item.id)} data-qa={rowQaId("cell-icd", item.id)} className="w-full px-2 py-1.5">
            <div id={rowQaId("icd-chip-list", item.id)} data-testid={rowQaId("icd-chip-list", item.id)} data-qa={rowQaId("icd-chip-list", item.id)} className="flex flex-wrap justify-center gap-1.5 mb-1">
              {shown.map((c, i) => {
                const s = kindStyle(c.kind);
                return (
                  <div
                    key={i}
                    id={rowQaId(`icd-chip-${i}`, item.id)}
                    data-testid={rowQaId(`icd-chip-${i}`, item.id)}
                    data-qa={rowQaId(`icd-chip-${i}`, item.id)}
                    data-qa-code={c.label}
                    data-qa-kind={c.kind}
                    style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 4px 2px 8px", borderRadius: 6, background: s.bg, border: `1px solid ${s.border}` }}
                  >
                    <span style={{ fontSize: 11, fontWeight: 600, color: s.color }}>{c.label}</span>
                    <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 5px", borderRadius: 4, background: "rgba(255,255,255,0.2)", color: s.color, opacity: 0.85, border: `1px solid ${s.border}` }}>{s.tag}</span>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-between border-t border-gray-100 pt-1">
              <span id={rowQaId("icd-count", item.id)} data-testid={rowQaId("icd-count", item.id)} data-qa={rowQaId("icd-count", item.id)} className="text-xs font-bold text-gray-500">{total}</span>
              {total > 3 && (
                <button
                  id={rowQaId("btn-show-more-icd-codes", item.id)}
                  data-testid={rowQaId("btn-show-more-icd-codes", item.id)}
                  data-qa={rowQaId("btn-show-more-icd-codes", item.id)}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIcdCodesDialogList(allDisplay);
                    setIcdCodesDialogTitle(`${item.name} – ICD Codes`);
                    setIcdCodesDialogOpen(true);
                  }}
                  className="text-xs text-blue-600 hover:text-blue-800 font-semibold"
                >
                  +{total - 3} more →
                </button>
              )}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "is_active",
      header: ({ column }) => (
        <Button {...qaAttrs("sort-is-active")} variant="ghost" className="w-full justify-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Is active{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const value = row.getValue("is_active") as boolean;
        const isDeleted = (row.original as any).is_deleted;
        return (
          <div className="w-full flex justify-center">
            <Button
              id={rowQaId("toggle-status", row.original.id)}
              data-testid={rowQaId("toggle-status", row.original.id)}
              data-qa={rowQaId("toggle-status", row.original.id)}
              data-qa-status={value ? "active" : "inactive"}
              data-qa-deleted={isDeleted ? "true" : "false"}
              className={`text-white ${isDeleted ? "opacity-40 cursor-not-allowed bg-gray-400" : value ? "bg-green-500 hover:bg-green-600" : "bg-red-500 hover:bg-red-600"}`}
              size="sm"
              disabled={!!isDeleted}
              onClick={() => !isDeleted && toggleActive(row.original)}
            >
              {value ? "Active" : "Inactive"}
            </Button>
          </div>
        );
      },
    },
    {
      id: "view",
      header: () => <div {...qaAttrs("column-view-header")} className="w-full text-center font-medium">View</div>,
      cell: ({ row }) => (
        <div className="w-full flex justify-center py-2">
          <button
            id={rowQaId("btn-view-details", row.original.id)}
            data-testid={rowQaId("btn-view-details", row.original.id)}
            data-qa={rowQaId("btn-view-details", row.original.id)}
            onClick={() => handleOpenDialog(row.original)}
            className="text-blue-600 hover:text-blue-800"
            title="View details"
            aria-label={`View Medical ICD Group ${row.original.id}`}
          >
            <i id={rowQaId("icon-view-details", row.original.id)} data-testid={rowQaId("icon-view-details", row.original.id)} data-qa={rowQaId("icon-view-details", row.original.id)} className="fa-solid fa-eye text-lg"></i>
          </button>
        </div>
      ),
    },
  ];

  return (
    <div
      {...qaAttrs("page")}
      className="h5-container mx-auto px-1 pt-16 pb-4"
      data-page-name="medical-icd-group"
    >

      <div {...qaAttrs("page-header")} className="flex justify-between items-center">
        <h5 {...qaAttrs("page-title")} className="mb-6 flex items-center gap-2 font-bold text-2xl mt-4 text-blue-900">
          <span {...qaAttrs("page-title-icon-wrapper")} className="icon-container flex items-center justify-center w-10 h-10 rounded-full bg-blue-900 text-white">
            <i {...qaAttrs("page-title-icon")} className="fa-solid fa-file-medical"></i>
          </span>
          Medical ICD grouping
        </h5>
      </div>

      <div {...qaAttrs("data-table-wrapper")}>
      <DataTable2
        qaIdPrefix={qaId("data-table")}
        columns={columns}
        data={filteredData}
        searchColumns={[]}
        addButtonLabel="Add new group"
        showAddButton={false}
        pageIndex={currentPage}
        onPageChange={setCurrentPage}
        manualPagination={true}
        pageCount={totalPages}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageSizeChange={(nextPageSize) => {
          setPageSize(nextPageSize);
          setCurrentPage(0);
        }}
        allowedPageSizes={[10, 25, 50, 100]}
        isLoading={isTableLoading}
        isRowInactive={(row: any) => row.is_active === false && !row.is_deleted}
        onAdd={handleAddClick}
        onEdit={handleEdit}
        onEditClick={handleEditClick}
        onDelete={(id) => {
          const item = items.result.find(i => i.id === id);
          if ((item as any)?.is_deleted) {
            setForceId(id);
            setForceAction("hardDelete");
            setForceError("");
            setForceDialogOpen(true);
          } else {
            removeItem(id);
          }
        }}
        onRestore={canManageDeleted ? restoreItem : undefined}
        canHardDelete={canManageDeleted}
        isRowDeleted={(row: any) => row.is_deleted === true}
        toolbarExtras={
          <div {...qaAttrs("toolbar-filters")} style={{ width: "100%", position: "relative" }}>
            <div {...qaAttrs("toolbar-filters-row")} style={{
              display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
              padding: "10px 160px 10px 16px",
              background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
              borderRadius: 12, border: "1px solid #e2e8f0",
              boxShadow: "0 1px 3px rgba(0,0,0,0.04)", marginBottom: 12,
            }}>
              <button {...qaAttrs("btn-toolbar-add-new-group")} type="button" onClick={handleAddClick} style={{
                display: "flex", alignItems: "center", gap: 7,
                padding: "8px 18px", fontSize: 13, fontWeight: 600,
                color: "#ffffff", background: "#1e3a8a",
                border: "none", borderRadius: 8, cursor: "pointer",
                whiteSpace: "nowrap", boxShadow: "0 2px 6px rgba(30,58,138,0.25)",
              }}>
                <svg {...qaAttrs("btn-toolbar-add-new-group-icon")} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M12 5v14M5 12h14"/>
                </svg>
                Add new group
              </button>

              <div style={{ width: 1, height: 28, background: "#cbd5e1" }} />

              <div {...qaAttrs("wrapper-search-name")} style={{ position: "relative" }}>
                <svg {...qaAttrs("icon-search-name")} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }}
                  width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
                </svg>
                <input
                  {...qaAttrs("input-search-name")}
                  type="text" value={filterName}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFilterName(v);
                    setCurrentPage(0);
                  }}
                  placeholder="Search name..."
                  style={{
                    width: 180, padding: "8px 36px 8px 38px", fontSize: 13,
                    borderRadius: 9999, outline: "none", transition: "all 0.2s ease",
                    border: filterName ? "2px solid #3b82f6" : "2px solid #e2e8f0",
                    background: filterName ? "#eff6ff" : "#ffffff",
                  }}
                />
                {filterName && (
                  <button {...qaAttrs("btn-clear-search-name")} type="button" onClick={() => { setFilterName(""); setCurrentPage(0); }}
                    style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#94a3b8", padding: 2 }}>
                    <svg {...qaAttrs("icon-clear-search-name")} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
                  </button>
                )}
              </div>

              <div style={{ width: 1, height: 28, background: "#cbd5e1" }} />

              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span {...qaAttrs("label-status-filter")} style={{ fontSize: 11, fontWeight: 600, color: "#64748b", letterSpacing: "0.05em" }}>Status</span>
                <div {...qaAttrs("status-filter-wrapper")} style={{ display: "flex", alignItems: "center", padding: 4, background: "#e2e8f0", borderRadius: 9999 }}>
                  {[{ value: "", label: "All" }, { value: "true", label: "Active" }, { value: "false", label: "Inactive" }].map((option) => (
                    <button
                      key={option.value}
                      id={qaId(`btn-filter-status-${option.value || "all"}`)}
                      data-testid={qaId(`btn-filter-status-${option.value || "all"}`)}
                      data-qa={qaId(`btn-filter-status-${option.value || "all"}`)}
                      data-qa-selected={filterStatus === option.value ? "true" : "false"}
                      type="button"
                      onClick={() => { setFilterStatus(option.value); setCurrentPage(0); }}
                      style={{
                        display: "flex", alignItems: "center", gap: 6,
                        padding: "6px 14px", fontSize: 13, fontWeight: 500,
                        borderRadius: 9999, border: "none", cursor: "pointer", transition: "all 0.2s ease",
                        background: filterStatus === option.value
                          ? option.value === "true" ? "#10b981" : option.value === "false" ? "#94a3b8" : "#ffffff"
                          : "transparent",
                        color: filterStatus === option.value ? option.value === "" ? "#334155" : "#ffffff" : "#64748b",
                        boxShadow: filterStatus === option.value ? "0 2px 4px rgba(0,0,0,0.1)" : "none",
                      }}>
                      {option.value === "true" && filterStatus === "true" && (
                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#ffffff" }} />
                      )}
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ width: 1, height: 28, background: "#cbd5e1" }} />

              <div {...qaAttrs("wrapper-search-icd")} style={{ position: "relative" }}>
                <svg {...qaAttrs("icon-search-icd")} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }}
                  width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
                </svg>
                <input
                  {...qaAttrs("input-search-icd")}
                  type="text" value={icdSearch}
                  onChange={(e) => {
                    const v = e.target.value;
                    setIcdSearch(v);
                    setCurrentPage(0);
                  }}
                  placeholder="Search ICD, medical group..."
                  style={{
                    width: 200, padding: "8px 36px 8px 38px", fontSize: 13,
                    borderRadius: 9999, outline: "none", transition: "all 0.2s ease",
                    border: icdSearch ? "2px solid #3b82f6" : "2px solid #e2e8f0",
                    background: icdSearch ? "#eff6ff" : "#ffffff",
                  }}
                />
                {icdSearch && (
                  <button {...qaAttrs("btn-clear-search-icd")} type="button" onClick={() => { setIcdSearch(""); setCurrentPage(0); }}
                    style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#94a3b8", padding: 2 }}>
                    <svg {...qaAttrs("icon-clear-search-icd")} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
                  </button>
                )}
              </div>

              <div style={{ flex: 1 }} />
            </div>
          </div>
        }
      />
      </div>

      {/* ─── View details modal ──────────────────────────────────────────────── */}
      <Dialog open={Boolean(selectedItem)} onOpenChange={(open) => { if (!open) handleCloseDialog(); }}>
        <DialogContent
          {...qaAttrs("dialog-view-details")}
          className="max-w-[780px] max-h-[92vh] flex flex-col p-0 overflow-hidden bg-white border-none rounded-2xl"
          style={{ boxShadow: "0 25px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)" }}
        >
          {selectedItem && (
            <>
              {/* ── Header ── */}
              <div {...qaAttrs("view-details-header")} style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #2563eb 100%)", padding: "24px 28px 20px", flexShrink: 0, position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", top: -40, right: -40, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
                <div style={{ position: "absolute", bottom: -30, left: -20, width: 100, height: 100, borderRadius: "50%", background: "rgba(255,255,255,0.03)" }} />

                <div style={{ display: "flex", alignItems: "flex-start", gap: 16, position: "relative", zIndex: 1 }}>
                  <div {...qaAttrs("view-details-icon-wrapper")} style={{ width: 48, height: 48, borderRadius: 14, background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, color: "#fff", flexShrink: 0, border: "1px solid rgba(255,255,255,0.1)" }}>
                    <i {...qaAttrs("view-details-icon")} className="fa-solid fa-file-medical" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 2 }}>
                      <h2 {...qaAttrs("view-details-name")} style={{ fontSize: 20, fontWeight: 700, color: "#fff", margin: 0, letterSpacing: "-0.01em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {selectedItem.name}
                      </h2>
                      <span {...qaAttrs("view-details-status-badge")} data-qa-status={selectedItem.is_active ? "active" : "inactive"} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 12px", borderRadius: 9999, fontSize: 11, fontWeight: 600, flexShrink: 0, background: selectedItem.is_active ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.15)", color: selectedItem.is_active ? "#86efac" : "#fca5a5", border: `1px solid ${selectedItem.is_active ? "rgba(34,197,94,0.25)" : "rgba(239,68,68,0.2)"}` }}>
                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} />
                        {selectedItem.is_active ? "Active" : "Inactive"}
                      </span>
                      {(selectedItem as any).is_deleted && (
                        <span {...qaAttrs("view-details-deleted-badge")} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 12px", borderRadius: 9999, fontSize: 11, fontWeight: 600, flexShrink: 0, background: "rgba(239,68,68,0.15)", color: "#fca5a5", border: "1px solid rgba(239,68,68,0.2)" }}>
                          <i {...qaAttrs("view-details-deleted-icon")} className="fa-solid fa-trash" style={{ fontSize: 10 }} /> Deleted
                        </span>
                      )}
                    </div>
                    <p {...qaAttrs("view-details-group-id")} style={{ fontSize: 12, color: "rgba(147,197,253,0.8)", margin: 0, fontWeight: 400 }}>
                      Group ID: <span style={{ fontWeight: 600, color: "#93c5fd" }}>#{selectedItem.id}</span>
                    </p>
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginTop: 18, position: "relative", zIndex: 1 }}>
                  {[
                    { icon: "fa-file-medical", label: "ICD Codes", value: `${(selectedItem.icd || []).length} codes`, accent: "#60a5fa" },
                    { icon: "fa-layer-group", label: "Medical Groups", value: `${(selectedItem.medical_groups || []).length}`, accent: "#a78bfa" },
                    { icon: "fa-align-left", label: "Description", value: selectedItem.description ? "Available" : "None", accent: "#34d399" },
                  ].map((card, i) => (
                    <div key={i} style={{ background: "rgba(255,255,255,0.07)", backdropFilter: "blur(6px)", borderRadius: 12, padding: "12px 14px", border: "1px solid rgba(255,255,255,0.08)" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                        <i className={`fa-solid ${card.icon}`} style={{ fontSize: 11, color: card.accent, opacity: 0.9 }} />
                        <span style={{ fontSize: 10, color: "rgba(147,197,253,0.7)", fontWeight: 500, letterSpacing: "0.03em" }}>{card.label}</span>
                      </div>
                      <span style={{ fontSize: 15, fontWeight: 700, color: card.accent }}>{card.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Body ── */}
              <div {...qaAttrs("view-details-body")} style={{ overflowY: "auto", flex: 1, background: "#f8fafc", padding: "6px 0 16px" }}>

                {selectedItem.description && (
                  <>
                    <div style={{ padding: "20px 28px 16px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                        <div style={{ width: 28, height: 28, borderRadius: 8, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #bbf7d0" }}>
                          <i className="fa-solid fa-align-left" style={{ fontSize: 12, color: "#16a34a" }} />
                        </div>
                        <span {...qaAttrs("view-details-description-title")} style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Description</span>
                      </div>
                      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                        <p {...qaAttrs("view-details-description")} style={{ margin: 0, fontSize: 14, color: "#334155", lineHeight: 1.6 }}>{selectedItem.description}</p>
                      </div>
                    </div>
                    <div style={{ height: 1, background: "#e2e8f0", margin: "0 28px" }} />
                  </>
                )}

                <div style={{ padding: "20px 28px 16px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #dbeafe" }}>
                      <i className="fa-solid fa-file-medical" style={{ fontSize: 12, color: "#3b82f6" }} />
                    </div>
                    <span {...qaAttrs("view-details-icd-codes-title")} style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>ICD Codes</span>
                    <span {...qaAttrs("view-details-icd-codes-count")} style={{ fontSize: 11, color: "#94a3b8", fontWeight: 500 }}>{(selectedItem.icd || []).length} items</span>
                  </div>
                  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                    <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 6 }}>
                      {(selectedItem.icd || []).length > 0
                        ? (selectedItem.icd || []).map((code: string, i: number) => (
                            <span key={i} id={qaId(`view-details-icd-code-${i}`)} data-testid={qaId(`view-details-icd-code-${i}`)} data-qa={qaId(`view-details-icd-code-${i}`)} data-qa-code={code} style={{ display: "inline-flex", alignItems: "center", padding: "3px 10px", borderRadius: 9999, fontSize: 11, fontWeight: 500, background: "#eff6ff", color: "#1d4ed8", border: "0.5px solid #bfdbfe" }}>{code}</span>
                          ))
                        : <span style={{ color: "#94a3b8", fontSize: 12, fontStyle: "italic" }}>No ICD codes</span>}
                    </div>
                  </div>
                </div>

                {(selectedItem.medical_groups || []).length > 0 && (
                  <>
                    <div style={{ height: 1, background: "#e2e8f0", margin: "0 28px" }} />
                    <div style={{ padding: "20px 28px 16px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                        <div style={{ width: 28, height: 28, borderRadius: 8, background: "#faf5ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #e9d5ff" }}>
                          <i className="fa-solid fa-layer-group" style={{ fontSize: 12, color: "#a855f7" }} />
                        </div>
                        <span {...qaAttrs("view-details-medical-groups-title")} style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Medical Groups</span>
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 6 }}>
                        {(selectedItem.medical_groups || []).map((g: any, i: number) => (
                          <span key={i} id={qaId(`view-details-medical-group-${i}`)} data-testid={qaId(`view-details-medical-group-${i}`)} data-qa={qaId(`view-details-medical-group-${i}`)} style={{ display: "inline-flex", alignItems: "center", padding: "3px 10px", borderRadius: 9999, fontSize: 11, fontWeight: 500, background: "#faf5ff", color: "#7e22ce", border: "0.5px solid #e9d5ff" }}>{g.name || g}</span>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                <div style={{ height: 1, background: "#e2e8f0", margin: "0 28px" }} />

                <div style={{ padding: "20px 28px 8px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #bbf7d0" }}>
                      <i className="fa-solid fa-clock-rotate-left" style={{ fontSize: 12, color: "#16a34a" }} />
                    </div>
                    <span {...qaAttrs("view-details-audit-title")} style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Audit Trail</span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                    <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", letterSpacing: "0.02em" }}>Created By</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg, #3b82f6, #2563eb)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                          {String((selectedItem as any).created_by ?? "?")[0].toUpperCase()}
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{resolveUserName((selectedItem as any).created_by)}</span>
                      </div>
                    </div>
                    <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", letterSpacing: "0.02em" }}>Created On</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                        <i className="fa-regular fa-calendar" style={{ fontSize: 14, color: "#3b82f6" }} />
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                          {(selectedItem as any).createdat
                            ? new Date((selectedItem as any).createdat).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
                            : "—"}
                        </span>
                      </div>
                    </div>
                    <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", letterSpacing: "0.02em" }}>Last Updated</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                        <i className="fa-regular fa-clock" style={{ fontSize: 14, color: "#10b981" }} />
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                          {(selectedItem as any).updatedat
                            ? new Date((selectedItem as any).updatedat).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
                            : "—"}
                        </span>
                      </div>
                    </div>
                  </div>
              {Array.isArray(selectedItem.updated_by) && selectedItem.updated_by.length > 0 && (
  <div style={{ gridColumn: "1 / -1", background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", marginTop: 12 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
      <i className="fa-solid fa-pen-to-square" style={{ fontSize: 11, color: "#10b981" }} />
      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.02em" }}>Update History</span>
      <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#f0fdf4", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0", marginLeft: "auto" }}>
        {selectedItem.updated_by.length} update{selectedItem.updated_by.length !== 1 ? "s" : ""}
      </span>
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {(selectedItem.updated_by as { id?: number; at?: string }[]).slice().reverse().slice(0, showAllUpdates ? undefined : 4).map((entry, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, background: i === 0 ? "#f0fdf4" : "#f8fafc", border: `1px solid ${i === 0 ? "#bbf7d0" : "#e2e8f0"}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: i === 0 ? "linear-gradient(135deg, #10b981, #059669)" : "linear-gradient(135deg, #94a3b8, #64748b)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
              {String(resolveUserName(entry.id) ?? "?")[0].toUpperCase()}
            </div>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{resolveUserName(entry.id)}</span>
            {i === 0 && <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#dcfce7", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0" }}>Latest</span>}
          </div>
          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>
            {entry.at ? new Date(entry.at).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
          </span>
        </div>
      ))}
      {selectedItem.updated_by.length > 4 && (
        <div style={{ textAlign: "center", padding: "6px 0" }}>
          <button {...qaAttrs("btn-toggle-update-history")} onClick={() => setShowAllUpdates(v => !v)} style={{ fontSize: 11, color: "#3b82f6", fontWeight: 600, background: "none", border: "none", cursor: "pointer" }}>
            {showAllUpdates ? "Show less ↑" : `+${selectedItem.updated_by.length - 4} more updates ↓`}
          </button>
        </div>
      )}
    </div>
  </div>
)}
                </div>
              </div>

              {/* ── Footer ── */}
              <div style={{ padding: "14px 28px", borderTop: "1px solid #e2e8f0", background: "#fff", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10, flexShrink: 0 }}>
                <Button {...qaAttrs("btn-close-view-details")} onClick={handleCloseDialog} style={{ background: "linear-gradient(135deg, #1e3a8a, #2563eb)", color: "#fff", fontWeight: 600, padding: "8px 28px", borderRadius: 10, boxShadow: "0 2px 8px rgba(37,99,235,0.3)" }}>Close Details</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ─── Add / Edit Dialog ───────────────────────────────────────────────── */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent {...qaAttrs("dialog-add-edit")} className="max-w-2xl h-auto max-h-[92vh] flex flex-col overflow-hidden p-0">

          {/* ── Header ── */}
          <div {...qaAttrs("add-edit-dialog-header")} style={{
            background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #2563eb 100%)",
            padding: "22px 28px 18px",
            flexShrink: 0,
            position: "relative",
            overflow: "hidden",
          }}>
            <div style={{ position: "absolute", top: -40, right: -40, width: 140, height: 140, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
            <div style={{ position: "absolute", bottom: -24, left: -16, width: 90, height: 90, borderRadius: "50%", background: "rgba(255,255,255,0.03)" }} />
            <div style={{ display: "flex", alignItems: "center", gap: 14, position: "relative", zIndex: 1 }}>
              <div {...qaAttrs("add-edit-dialog-icon-wrapper")} style={{
                width: 44, height: 44, borderRadius: 12,
                background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 20, color: "#fff", flexShrink: 0,
                border: "1px solid rgba(255,255,255,0.1)",
              }}>
                <i id={qaId(editIndex === null ? "add-edit-dialog-add-icon" : "add-edit-dialog-edit-icon")} data-testid={qaId(editIndex === null ? "add-edit-dialog-add-icon" : "add-edit-dialog-edit-icon")} data-qa={qaId(editIndex === null ? "add-edit-dialog-add-icon" : "add-edit-dialog-edit-icon")} className={editIndex === null ? "fa-solid fa-plus" : "fa-solid fa-pen"} />
              </div>
              <div>
                <h2 {...qaAttrs("add-edit-dialog-title")} style={{ fontSize: 18, fontWeight: 700, color: "#fff", margin: 0, letterSpacing: "-0.01em" }}>
                  {editIndex === null ? "Add medical ICD group" : "Edit medical ICD group"}
                </h2>
                <p {...qaAttrs("add-edit-dialog-subtitle")} style={{ fontSize: 12, color: "rgba(147,197,253,0.8)", margin: 0, marginTop: 2 }}>
                  {editIndex === null
                    ? ""
                    : `Editing group #${editIndex}`}
                </p>
              </div>
            </div>
          </div>

          {/* ── Scrollable body ── */}
          <div {...qaAttrs("add-edit-dialog-body")} className="flex-1 overflow-y-auto px-6 py-5 space-y-0" style={{ background: "#f8fafc" }}>

            {/* Section 1 — Basic info */}
            <SectionCard num={1} title="Basic information" subtitle="Name and description for this group" automationId={qaId("section-basic-information")}>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label {...qaAttrs("label-name")} htmlFor={qaId("input-name")} className="mb-1.5 block text-sm font-medium text-gray-700">
                    Name <span className="text-red-500 ml-0.5">*</span>
                  </Label>
                  <div className="relative">
                    <div style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", pointerEvents: "none" }}>
                    </div>
                    <Input
                      {...qaAttrs("input-name")}
                      value={Name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Enter a unique group name (min. 3 characters)"
                      className="pl-9"
                      style={{ borderColor: Name.trim().length > 0 && Name.trim().length < 3 ? "#f87171" : undefined }}
                    />
                  </div>
                  {Name.trim().length > 0 && Name.trim().length < 3 && (
                    <p {...qaAttrs("error-name-min-length")} className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <i {...qaAttrs("error-name-min-length-icon")} className="fa-solid fa-circle-exclamation text-[10px]" /> Name must be at least 3 characters
                    </p>
                  )}
                </div>

                <div>
                  <Label {...qaAttrs("label-description")} htmlFor={qaId("input-description")} className="mb-1.5 block text-sm font-medium text-gray-700">
                    Description
                  </Label>
                  <div className="relative">
                    <div style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", pointerEvents: "none" }}>
                    </div>
                    <Input
                      {...qaAttrs("input-description")}
                      value={Description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Optional description for this group"
                      className="pl-9"
                    />
                  </div>
                </div>
              </div>
            </SectionCard>

            {/* Section 2 — ICD codes */}
            <SectionCard num={2} title="ICD codes" subtitle="Select the ICD codes that belong to this group (required)" automationId={qaId("section-icd-codes")}>
              <div {...qaAttrs("icd-selector-card")} className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div style={{ width: 24, height: 24, borderRadius: 6, background: "#1d4ed8", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <i className="fa-solid fa-file-medical text-white" style={{ fontSize: 10 }} />
                  </div>
                  <span {...qaAttrs("label-icd-selector")} className="text-sm font-semibold text-blue-900">List of ICD</span>
                  <span {...qaAttrs("hint-icd-selector-required")} className="text-xs text-blue-500 ml-auto">At least 1 code required</span>
                </div>
                <div {...qaAttrs("wrapper-icd-selector")}>
                  <Listofmedicalcode
                    key={editIndex}
                    onChange={handlemedicalcodeChange}
                    description={IcdDescription}
                    defaultValue={medicalcode}
                    clear={clearmedicalcode}
                  />
                </div>
              </div>
            </SectionCard>

            {/* Section 3 — Medical ICD groups */}
            <SectionCard num={3} title="Medical ICD groups" subtitle="Optionally link this group to existing medical ICD groups" automationId={qaId("section-medical-icd-groups")}>
              <div {...qaAttrs("medical-group-selector-card")} className="rounded-lg border border-purple-200 bg-purple-50 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div style={{ width: 24, height: 24, borderRadius: 6, background: "#7e22ce", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <i className="fa-solid fa-layer-group text-white" style={{ fontSize: 10 }} />
                  </div>
                  <span {...qaAttrs("label-medical-group-selector")} className="text-sm font-semibold text-purple-900">Medical ICD groups</span>
                  <span {...qaAttrs("hint-medical-group-optional")} className="text-xs text-purple-400 ml-auto">Optional</span>
                </div>
                <div {...qaAttrs("wrapper-medical-group-selector")}>
                  <ListofMedicalGroup
                    onChange={handlemedicalicdChange}
                    defaultValue={medicalIcdGroups}
                    clear={clearmedicalIcdGroups}
                  />
                </div>
              </div>
            </SectionCard>

          </div>

          {/* ── Footer ── */}
          <div style={{
            padding: "14px 24px",
            borderTop: "1px solid #e2e8f0",
            background: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 10,
            flexShrink: 0,
          }}>
            <Button
              {...qaAttrs("btn-cancel-add-edit-dialog")}
              variant="outline"
              onClick={() => { setEditIndex(null); clear(); setIsDialogOpen(false); }}
              style={{ padding: "8px 20px", borderRadius: 9, fontWeight: 500, fontSize: 13 }}
            >
              Cancel
            </Button>
            <Button
              id={qaId(editIndex === null ? "btn-add-group-submit" : "btn-update-group-submit")}
              data-testid={qaId("btn-save-group")}
              data-qa={qaId(editIndex === null ? "btn-add-group-submit" : "btn-update-group-submit")}
              data-qa-action={editIndex === null ? "add-group" : "update-group"}
              onClick={() => (editIndex === null ? addItem() : updateItem(editIndex))}
              style={{
                background: "linear-gradient(135deg, #1e3a8a, #2563eb)",
                color: "#fff", fontWeight: 600,
                padding: "8px 24px", borderRadius: 9,
                boxShadow: "0 2px 8px rgba(37,99,235,0.3)",
                fontSize: 13,
              }}
            >
              <i id={qaId(editIndex === null ? "icon-add-group-submit" : "icon-update-group-submit")} data-testid={qaId(editIndex === null ? "icon-add-group-submit" : "icon-update-group-submit")} data-qa={qaId(editIndex === null ? "icon-add-group-submit" : "icon-update-group-submit")} className={`fa-solid ${editIndex === null ? "fa-plus" : "fa-floppy-disk"} mr-2`} />
              {editIndex === null ? "Add Group " : "Update Group"} 
            </Button>
          </div>

        </DialogContent>
      </Dialog>

      {/* ─── View ICD codes dialog ───────────────────────────────────────────── */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent {...qaAttrs("dialog-all-icd-codes")}> 
          <DialogHeader><DialogTitle {...qaAttrs("dialog-all-icd-codes-title")}>All ICD codes</DialogTitle></DialogHeader>
          <div {...qaAttrs("all-icd-codes-list")} className="space-y-1 max-h-64 overflow-auto my-2">
            {viewCodes.map((code, index) => <div key={code} id={qaId(`all-icd-code-item-${index}`)} data-testid={qaId(`all-icd-code-item-${index}`)} data-qa={qaId(`all-icd-code-item-${index}`)} data-qa-code={code} className="text-sm">{code}</div>)}
          </div>
          <DialogFooter className="flex justify-between">
            <Button {...qaAttrs("btn-download-icd-csv")} variant="outline" onClick={() => {
              const csv = viewCodes.join("\r\n");
              const blob = new Blob([csv], { type: "text/csv" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a"); a.href = url; a.download = "icd_codes.csv"; a.click();
              URL.revokeObjectURL(url);
            }}>Download CSV</Button>
            <Button {...qaAttrs("btn-close-all-icd-codes")} onClick={() => setIsViewDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── ICD Codes expand dialog ─────────────────────────────────────────── */}
      <Dialog open={icdCodesDialogOpen} onOpenChange={setIcdCodesDialogOpen}>
        <DialogContent {...qaAttrs("dialog-expanded-icd-codes")} className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle {...qaAttrs("dialog-expanded-icd-codes-title")}>{icdCodesDialogTitle} ({icdCodesDialogList.length})</DialogTitle>
          </DialogHeader>
          <div {...qaAttrs("expanded-icd-codes-body")} className="mt-3 overflow-y-auto max-h-[calc(80vh-120px)] space-y-4">
            {icdCodesDialogList.filter(x => x.kind === "icd").length > 0 && (
              <div>
                <span className="text-xs font-bold text-gray-600">ICD Codes</span>
                <div className="flex flex-wrap gap-2 mt-1">
                  {icdCodesDialogList.filter(x => x.kind === "icd").map((c, i) => (
                    <span key={i} id={qaId(`expanded-icd-code-${i}`)} data-testid={qaId(`expanded-icd-code-${i}`)} data-qa={qaId(`expanded-icd-code-${i}`)} data-qa-code={c.label} style={{ padding: "4px 12px", background: "#011934", color: "white", borderRadius: 6, fontSize: 12, fontWeight: 600 }}>{c.label}</span>
                  ))}
                </div>
              </div>
            )}
            {icdCodesDialogList.filter(x => x.kind === "medical").length > 0 && (
              <div>
                <span className="text-xs font-bold text-green-700">Medical Groups</span>
                <div className="flex flex-wrap gap-2 mt-1">
                  {icdCodesDialogList.filter(x => x.kind === "medical").map((c, i) => (
                    <span key={i} id={qaId(`expanded-medical-group-${i}`)} data-testid={qaId(`expanded-medical-group-${i}`)} data-qa={qaId(`expanded-medical-group-${i}`)} style={{ padding: "4px 12px", background: "#f0fdf4", color: "#15803d", borderRadius: 6, fontSize: 12, fontWeight: 600, border: "1px solid #86efac" }}>{c.label}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end pt-3 border-t mt-3">
            <Button {...qaAttrs("btn-close-expanded-icd-codes")} onClick={() => setIcdCodesDialogOpen(false)} className="bg-blue-700 hover:bg-blue-800">Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── Force / Hard Delete Dialog ───────────────────────────────────────── */}
      {forceDialogOpen && (
        <div {...qaAttrs("dialog-force-delete-overlay")} style={{ position: "fixed", inset: 0, background: "#00000088", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)" }}>
          <div {...qaAttrs("dialog-force-delete")} data-qa-force-action={forceAction ?? ""} data-qa-force-id={forceId ?? ""} style={{ background: "#fff", borderRadius: 16, padding: 32, width: 440, boxShadow: "0 20px 50px rgba(0,0,0,0.3)", border: "1px solid #fecaca" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 12 }}>
              <div {...qaAttrs("force-dialog-alert-icon-wrapper")} style={{ width: 60, height: 60, borderRadius: "50%", background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i {...qaAttrs("force-dialog-alert-icon")} className="fa-solid fa-triangle-exclamation" style={{ fontSize: 28, color: "#dc2626" }}></i>
              </div>

              {forceAction === "hardDelete" && (
                <>
                  <h3 {...qaAttrs("hard-delete-alert-title")} style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>Permanent Delete</h3>
                  <p {...qaAttrs("hard-delete-alert-message")} style={{ fontSize: 13, color: "#64748b", margin: 0, lineHeight: 1.6 }}>
                    This will <strong style={{ color: "#dc2626" }}>permanently delete</strong> the record.<br />This action <strong>cannot be undone</strong>.
                  </p>
                  {forceError && (
                    <div {...qaAttrs("hard-delete-error-container")} style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                        <i {...qaAttrs("hard-delete-error-icon")} className="fa-solid fa-circle-info" style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}></i>
                        <p {...qaAttrs("hard-delete-error-message")} style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6 }}>{forceError}</p>
                      </div>
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
                    <button
                      {...qaAttrs("btn-cancel-hard-delete")}
                      data-qa-action="cancel-hard-delete"
                      onClick={() => { setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null); }}
                      style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                      Cancel
                    </button>
                    <button
                      {...qaAttrs("btn-confirm-hard-force-delete")}
                      data-qa-action="confirm-hard-force-delete"
                      onClick={() => forceId !== null && permanentDeleteItem(forceId, true)}
                      style={{ flex: 1, background: "#7f1d1d", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
                      <i {...qaAttrs("icon-confirm-hard-force-delete")} className="fa-solid fa-bolt mr-2"></i>Force Delete Anyway
                    </button>
                  </div>
                </>
              )}

              {(forceAction === "softDelete" || forceAction === "status") && (
                <>
                  <h3 {...qaAttrs(forceAction === "status" ? "status-force-alert-title" : "soft-delete-alert-title")} style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>
                    {forceAction === "status" ? "Status Change Warning" : "Record Is In Use"}
                  </h3>
                  <div {...qaAttrs(forceAction === "status" ? "status-force-error-container" : "soft-delete-error-container")} style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                      <i {...qaAttrs(forceAction === "status" ? "status-force-error-icon" : "soft-delete-error-icon")} className="fa-solid fa-circle-info" style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}></i>
                      <p {...qaAttrs(forceAction === "status" ? "status-force-error-message" : "soft-delete-error-message")} style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6 }}>{forceError}</p>
                    </div>
                  </div>
                  <p {...qaAttrs(forceAction === "status" ? "status-force-confirmation-message" : "soft-delete-confirmation-message")} style={{ fontSize: 13, color: "#64748b", margin: 0 }}>
                    Do you want to <strong style={{ color: "#dc2626" }}>force {forceAction === "status" ? "update status" : "delete"}</strong> anyway?
                  </p>
                  <div style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
                    <button
                      {...qaAttrs(forceAction === "status" ? "btn-cancel-status-force-update" : "btn-cancel-soft-delete")}
                      data-qa-action={forceAction === "status" ? "cancel-status-force-update" : "cancel-soft-delete"}
                      onClick={() => { setForceDialogOpen(false); setForceId(null); setForceTargetItem(null); setForceError(""); setForceAction(null); }}
                      style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                      Cancel
                    </button>
                    <button
                      {...qaAttrs(forceAction === "status" ? "btn-confirm-status-force-update" : "btn-confirm-soft-force-delete")}
                      data-qa-action={forceAction === "status" ? "confirm-status-force-update" : "confirm-soft-force-delete"}
                      onClick={() => {
                        if (!forceId) return;
                        if (forceAction === "softDelete") forceSoftDelete(forceId);
                        else if (forceAction === "status" && forceTargetItem) toggleActive(forceTargetItem, true);
                      }}
                      style={{ flex: 1, background: "#dc2626", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
                      <i {...qaAttrs(forceAction === "status" ? "icon-confirm-status-force-update" : "icon-confirm-soft-force-delete")} className="fa-solid fa-bolt" style={{ marginRight: 8 }}></i>
                      Force {forceAction === "status" ? "Update" : "Delete"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <div {...qaAttrs("toaster-wrapper")}>
        <Toaster />
      </div>
    </div>
  );
};

export default MedicalIcdGroups;
